import { ChevronLeft } from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router";
import Box from "@mui/material/Box";
import { Button } from "@components/ui/button";
import SimpleBar from "simplebar-react";
import { Textarea } from "@components/ui/textarea";
import { SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import "simplebar-react/dist/simplebar.min.css";
import { addVpsProfile, VpsProfile } from "@redux/slices/strategicPlanning";
import useAuth from "@hooks/useAuth";
import LoadingScreen from "@components/LoadingScreen";
import { Input } from "@components/ui/input";
import { addNotification } from "@redux/slices/notifications";

const AddVps: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const [formData, setFormData] = useState<VpsProfile>({
    user_id: user?.id,
    user_name: user?.first_name + " " + user?.last_name,
    status: "",
    name:"",
    question1: "",
    question2: "",
    question3: "",
    question4: "",
    question5: "",
    question6: "",
    question7: "",
    question8: "",
    question9: "",
    question10: "",
  });

  const icpSchema = z.object({
    question1: z.string(),
    name: z.string(),
    question2: z.string(),
    question3: z.string(),
    question4: z.string(),
    question5: z.string(),
    question6: z.string(),

    question7: z.string(),
    question8: z.string(),
    question9: z.string(),
    question10: z.string(),
  });

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  type ICPFormData = z.infer<typeof icpSchema>;

  // Initialize React Hook Form
  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
    getValues,
  } = useForm<ICPFormData>({
    resolver: zodResolver(icpSchema),
    mode: "onBlur",
  });

  const onSubmit: SubmitHandler<ICPFormData> = (data) => {
    addProfile();
  };

  const addProfile = async () => {
    try {
      setLoading(true);

      let updatedFormData = { ...formData };
      if (
        formData?.name != "" &&
        formData?.question1 != "" &&
        formData.question2 != "" &&
        formData.question3 != "" &&
        formData.question4 != "" &&
        formData.question5 != "" &&
        formData.question6 != "" &&
        formData.question7 != "" &&
        formData.question8 != "" &&
        formData.question9 != "" &&
        formData.question10 != ""
      ) {
        console.log("first");
        updatedFormData = {
          ...updatedFormData,
          status: "completed",
        };
        await addNotification("Vps Added", `${user?.email} added vps `);
      } else {
        updatedFormData = {
          ...updatedFormData,
          status: "draft",
        };
      }

      await addVpsProfile(updatedFormData, "Profile added successfully");

      setLoading(false);
      navigate(`/strategicPlanning/vps/${user?.id}`);
    } catch (error) {
      console.error("Error in addProfile:", error);
      setLoading(false);
    }
  };

  return (
    <div>
      {loading ? (
        <LoadingScreen />
      ) : (
        <div>
          {/* Header */}
          <div className="flex justify-between items-center mb-5">
            <h4 className="text-xl font-bold flex items-center -ms-2">
              <ChevronLeft
                size={22}
                className="me-3 cursor-pointer"
                onClick={() => navigate(-1)}
              />
              Add Value Propositions and Strengths (VPS)
            </h4>
            <div className="flex items-start">
              <p className="mt-2">Profile Name: </p>
              <div>
                <Input
                  {...register("name")}
                  className="w-full ms-2"
                  placeholder="Profile Name"
                  value={formData?.name}
                  onChange={handleInputChange}
                  // value={}
                />
                {errors.name && (
                  <p className="text-red-500 text-sm ms-2 mt-1">
                    {errors.name.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Form Container */}
          <div className="px-56 mt-10">
            <Box sx={{ width: "100%" }}>
              <SimpleBar style={{ maxHeight: 450 }}>
                <div className="px-5">
                  {/* Question 1 */}
                  <div className="mb-4">
                    <p className="text-[#71717A] mb-2">
                      1. What specific advantages does your product/service
                      offer that your customers cannot find anywhere else?
                    </p>
                    <Textarea
                      {...register("question1")}
                      className="h-24 w-full"
                      placeholder="Your answer..."
                      value={formData?.question1}
                      onChange={handleInputChange}
                      // value={}
                    />
                    {errors.question1 && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.question1.message}
                      </p>
                    )}
                  </div>
                  {/* Question 2 */}
                  <div className="mb-4">
                    <p className="text-[#71717A] mb-2">
                      2. What are the top three reasons your customers chose
                      your product/service?
                    </p>
                    <Textarea
                      {...register("question2")}
                      className="h-24 w-full"
                      placeholder="Your answer..."
                      value={formData?.question2}
                      onChange={handleInputChange}
                    />
                    {errors.question2 && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.question2.message}
                      </p>
                    )}
                  </div>
                  {/* Question 3 */}
                  <div className="mb-4">
                    <p className="text-[#71717A] mb-2">
                      3. How do your products/services contribute to achieving
                      your customers' strategic goals?
                    </p>
                    <Textarea
                      {...register("question3")}
                      className="h-24 w-full"
                      placeholder="Your answer..."
                      value={formData?.question3}
                      onChange={handleInputChange}
                    />
                    {errors.question3 && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.question3.message}
                      </p>
                    )}
                  </div>
                  {/* Question 4 */}
                  <div className="mb-4">
                    <p className="text-[#71717A] mb-2">
                      4. What positive changes have your customers experienced
                      by using your products/services?
                    </p>
                    <Textarea
                      {...register("question4")}
                      className="h-24 w-full"
                      placeholder="Your answer..."
                      value={formData?.question4}
                      onChange={handleInputChange}
                    />
                    {errors.question4 && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.question4.message}
                      </p>
                    )}
                  </div>
                  {/* Question 5 */}
                  <div className="mb-4">
                    <p className="text-[#71717A] mb-2">
                      5. How would your customers describe the value they
                      receive from your product/service?
                    </p>
                    <Textarea
                      {...register("question5")}
                      className="h-24 w-full"
                      placeholder="Your answer..."
                      value={formData?.question5}
                      onChange={handleInputChange}
                    />
                    {errors.question5 && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.question5.message}
                      </p>
                    )}
                  </div>
                  {/* Question 6 */}
                  <div className="mb-4">
                    <p className="text-[#71717A] mb-2">
                      6. What problems or challenges did your customers face
                      before using your solution, and how were these resolved by
                      your product/service?
                    </p>
                    <Textarea
                      {...register("question6")}
                      className="h-24 w-full"
                      placeholder="Your answer..."
                      value={formData?.question6}
                      onChange={handleInputChange}
                    />
                    {errors.question6 && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.question6.message}
                      </p>
                    )}
                  </div>
                  {/* Question 7 */}
                  <div className="mb-4">
                    <p className="text-[#71717A] mb-2">
                      7. How does your product/service stand out in terms of
                      quality and performance compared to the competition?
                    </p>
                    <Textarea
                      {...register("question7")}
                      className="h-24 w-full"
                      placeholder="Your answer..."
                      value={formData?.question7}
                      onChange={handleInputChange}
                    />
                    {errors.question7 && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.question7.message}
                      </p>
                    )}
                  </div>
                  {/* Question 8 */}
                  <div className="mb-4">
                    <p className="text-[#71717A] mb-2">
                      8. What aspects of your customer service or support are
                      most appreciated by your customers?
                    </p>
                    <Textarea
                      {...register("question8")}
                      className="h-24 w-full"
                      placeholder="Your answer..."
                      value={formData?.question8}
                      onChange={handleInputChange}
                    />
                    {errors.question8 && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.question8.message}
                      </p>
                    )}
                  </div>
                  {/* Question 9 */}
                  <div className="mb-4">
                    <p className="text-[#71717A] mb-2">
                      9. What future developments or improvements do you plan to
                      further increase the value of your product/service?
                    </p>
                    <Textarea
                      {...register("question9")}
                      className="h-24 w-full"
                      placeholder="Your answer..."
                      value={formData?.question9}
                      onChange={handleInputChange}
                    />
                    {errors.question9 && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.question9.message}
                      </p>
                    )}
                  </div>
                  {/* Question 10 */}
                  <div className="mb-4">
                    <p className="text-[#71717A] mb-2">
                      10. How do you communicate the benefits and advantages of
                      your product/service to your potential and existing
                      customers?
                    </p>
                    <Textarea
                      {...register("question10")}
                      className="h-24 w-full"
                      placeholder="Your answer..."
                      value={formData?.question10}
                      onChange={handleInputChange}
                    />
                    {errors.question10 && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.question10.message}
                      </p>
                    )}
                  </div>
                </div>
              </SimpleBar>
              <div className="flex justify-end mt-5">
                <Button onClick={handleSubmit(onSubmit)}>Save</Button>
              </div>
            </Box>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddVps;
